<template>
  <div class="bg-gray-100 securtrack_alarmcentral">
    <div class="gps-track">
    <!-- Row with button for showing all alarms, input for test alarm and test alarm button -->
      <div class="flex">
        <div class="mapsettings w-4/5 pt-2 pl-3">
          <button @click="backToDashboard" :class="soundOn ? 'btn-secondary' : 'btn-blue'" class="py-1 mr-2 text-white rounded shadow transition duration-300">
            {{ $t('alarm_center_menu.back') }}
          </button>
          <button @click="toggleShowAlarm" :class="showAllAlarms ? 'btn-blue' : 'btn-secondary'" class="py-1 text-white rounded shadow transition duration-300" v-if="!showOnlyEvent">
            {{ showAllAlarms ? $t('alarm_center_menu.hide_all_alarms') : $t('alarm_center_menu.show_all_alarms') }}
          </button>
          <button @click="toggleSound" :class="soundOn ? 'btn-secondary' : 'btn-blue'" class="py-1 ml-2 text-white rounded shadow transition duration-300">
            {{ soundOn ? $t('alarm_center_menu.turn_off_sound') : $t('alarm_center_menu.turn_on_sound') }}
          </button>
          <button class="ml-3 text-sm "><b>{{ $t('alarm_center_menu.display_mode') }}</b>: {{ alarmPresentationText }}</button>
        </div>
        <div class="flex justify-end w-full">
          <div v-if="showTestInput" class="flex gap-2">
            <div class="pt-2 w-2/4">
              <input type="text" v-model="inputPhone" field_name="" :placeholder="$t('alarm_center_menu.phone_number')" class="py-1 pl-2 text-xs w-full bg-white rounded shadow"/>
            </div>
            <div class="pt-2">
              <input type="text" v-model="inputPassword" field_name="" :placeholder="$t('alarm_center_menu.authorization_code')" class="py-1 pl-2 text-xs w-full bg-white rounded shadow"/>
            </div>
            <div class="pt-2">
              <button @click="activateTestAlarm" class="py-1 btn-green">
                {{ $t('alarm_center_menu.activate') }}
              </button>
            </div>
          </div>
        </div>
        <div class="buttoncontainer pt-2 pl-2 pr-3">
          <button @click="toggleInput" :class="showTestInput ? 'btn-red' : 'btn-blue'" class="py-1 text-white rounded shadow transition duration-300">
            {{ showTestInput ? $t('alarm_center_menu.cancel') : $t('alarm_center_menu.test_alarm') }}
          </button>
        </div> 
      </div>
      <!-- Section for map and alarm info -->
      <div class="map-alarm-container">
        <div class="map-container w-2/3">
          <GmapMap :center="center" :zoom="zoom" map-type-id="terrain" class="google_map_wrapper_alarm_center shadow-inner" ref="map" :options="{ fullscreenControl: mapFullScreenControl }">
              <GmapInfoWindow :options="infoOptions" :position="infoWindowPos" :opened="infoWinOpen"  @closeclick="closeInfoWindow(infoOptions)"> </GmapInfoWindow>
            <GmapCluster :zoom-on-click="true">
              <GmapMarker v-for="(m, index) in markers_filtered" :position="m.position" :clickable="true" :draggable="false" @mouseover="toggleInfoWindow(m, index, 'mouseover')" @mouseout="toggleInfoWindow(m, index,'mouseout')" @click="focusAlarm(m.imei_number)" :key="index" :icon="{ url: require(`@/assets/icon/${m.icon}`), scaledSize: { width: 42, height: 50 }, anchor: { x: 21, y: 30 }  }"></GmapMarker>
            </GmapCluster>
            <GmapCircle v-for="(pin, index) in alarmCircles_filtered" :key="index" :center="pin.position" :visible="true" :options="pin.options"></GmapCircle>
            <GmapPolyline v-for="p in paths.push_button" :key="p.imei_number" :path="p.path" :options="{ strokeColor: 'red' }" @mouseover="showInfoWindow($event, p.infoText, true)" @mouseout="showInfoWindow($event, p.infoText, false)"></GmapPolyline>

            <GmapPolyline v-for="(p, i) in track_alarm_paths_filtered" :key="p.imei_number + `-${i}`" :path="p.path" :options="{ strokeColor: '#1a8de9' }" @mouseover="showInfoWindow($event, p.infoText, true)" @mouseout="showInfoWindow($event, p.infoText, false)"></GmapPolyline>
          </GmapMap>
        </div>
   
        <div class="right-panel w-1/3 flex flex-col">

          <!-- ===== Inline Voice Room Panel (EMERIT e-B10 / SecurApp active alarm only) ===== -->
          <div v-if="selectedAlarm && ['EMERIT e-B10', 'SecurApp'].includes(selectedAlarm.type) && selectedAlarm.pushButton" class="mb-6 border-gray-200 bg-white p-3">
            <!-- Header row -->
            <div class="flex items-center mb-2">
              <span
                class="inline-block w-2 h-2 rounded-full mr-1"
                :class="{
                  'bg-green-500': localVoiceStatus === 'active',
                  'bg-yellow-400 animate-pulse': localVoiceStatus === 'connecting',
                  'bg-gray-400': localVoiceStatus === 'idle' || localVoiceStatus === 'ended',
                  'bg-red-500': localVoiceStatus === 'error',
                }"
              ></span>
              <span class="text-xs font-semibold text-gray-700">Voice Room</span>
            </div>

            <!-- Idle / Ended: Join button -->
            <div v-if="localVoiceStatus === 'idle' || localVoiceStatus === 'ended'" class="flex items-center gap-2">
              <button class="btn-green text-xs py-1 px-3" @click.prevent="joinVoiceRoomInline">
                <BaseIcon icon="phone" class="mr-1" /> {{ $t('alarm_center_menu.voice_room_join') }}
              </button>
            </div>

            <!-- Error state -->
            <div v-else-if="localVoiceStatus === 'error'" class="flex items-center gap-2">
              <span class="text-xs text-red-500">{{ voiceErrorMsg }}</span>
              <button class="btn-green text-xs py-1 px-3 ml-2" @click.prevent="joinVoiceRoomInline">
                <BaseIcon icon="phone" class="mr-1" /> {{ $t('alarm_center_menu.voice_room_retry') }}
              </button>
            </div>

            <!-- Connecting state -->
            <div v-else-if="localVoiceStatus === 'connecting'" class="flex items-center gap-2">
              <span class="text-xs text-yellow-600 animate-pulse">{{ $t('alarm_center_menu.voice_room_connecting') }}</span>
            </div>

            <!-- Active: floating control bar -->
            <div v-else-if="localVoiceStatus === 'active'" class="rounded-xl bg-gray-100 border border-gray-200 p-2">
              <!-- Header: timer only -->
              <div class="flex items-center px-1 mb-2 gap-1 text-gray-600">
                <BaseIcon icon="clock" class="text-xs" />
                <span class="text-xs font-medium">{{ formattedCallDuration }}</span>
              </div>
              <!-- Three card buttons -->
              <div class="flex gap-2">
                <!-- Mic -->
                <button
                  @click.prevent="toggleMic"
                  class="flex-1 flex flex-col items-center justify-center gap-1 rounded-xl py-2 bg-white border border-gray-200 shadow-sm hover:bg-gray-50 transition"
                >
                  <BaseIcon :icon="micMuted ? 'microphone-slash' : 'microphone'" class="text-red-500" />
                  <span class="text-xxs text-gray-700">{{ micMuted ? $t('alarm_center_menu.voice_room_mic_muted') : $t('alarm_center_menu.voice_room_mic_on') }}</span>
                </button>
                <!-- Speaker -->
                <button
                  @click.prevent="toggleSpeaker"
                  class="flex-1 flex flex-col items-center justify-center gap-1 rounded-xl py-2 bg-white border border-gray-200 shadow-sm hover:bg-gray-50 transition"
                >
                  <BaseIcon :icon="speakerMuted ? 'volume-mute' : 'volume-up'" class="text-gray-500" />
                  <span class="text-xxs text-gray-700">{{ $t('alarm_center_menu.voice_room_speaker') }}</span>
                </button>
                <!-- End alarm -->
                <button
                  @click.prevent="hangupVoice"
                  class="flex-1 flex flex-col items-center justify-center gap-1 rounded-xl py-2 bg-white border border-gray-200 shadow-sm hover:bg-gray-50 transition"
                >
                  <BaseIcon icon="phone-slash" class="text-red-500" />
                  <span class="text-xxs text-gray-700">{{ $t('alarm_center_menu.voice_room_end_alarm') }}</span>
                </button>
              </div>
            </div>
          </div>
          <!-- ===== End Voice Room Panel ===== -->

          
          <div class="bg-white alarm-info shadow-md">
            <div class="table-header bg-gray-200 font-bold flex flex-row border-b border-secondary-300 flex-shrink-0">
              <div class="text-sm text-primary-700 ml-2">{{ $t('alarm_center_menu.alarm_information') }}</div>
            </div>
            <!-- Alarm Info -->
            <div v-if="selectedAlarm" class="flex-grow overflow-y-auto p-2">

              <div class="current_alarm_info">
                <div class="alarm-details">
                  <div class="w-full">
                    <p class="text-sm font-bold">
                      {{ selectedAlarm.alarm_name || ''  }}
                    </p>
                  </div>
                  <div class="w-full">
                    <p class="text-xs">
                      {{ selectedAlarm.customer_name || ''  }} - {{ selectedAlarm.unit_name || ''  }}
                    </p>
                    <span class="item text-xxs">
                      <span v-if="new RegExp('^SRT').test(selectedAlarm.type)" class="last-seen mr-1" v-bind:class="{ inactive: selectedAlarm.lastSeenTime > 1000 * 60 * 60 }">⬤</span>
                      <span v-if="new RegExp('^TWIG').test(selectedAlarm.type)" class="last-seen mr-1" v-bind:class="{ inactive: selectedAlarm.lastSeenTime > 1000 * 60 * 60 * 2.5 }">⬤</span>
                      <span v-if="new RegExp('^EMERIT').test(selectedAlarm.type)" class="last-seen mr-1" v-bind:class="{ inactive: selectedAlarm.lastSeenTime > 1000 * 60 * 60 }">⬤</span>
                      <span v-if="new RegExp('^TELTONIKA').test(selectedAlarm.type)" class="last-seen mr-1" v-bind:class="{ inactive: selectedAlarm.lastSeenTime > 1000 * 60 * 60 }">⬤</span>
                      {{ selectedAlarm.lastSeen }}
                    </span>
                  </div>
                  <hr class="custom-line">
                  <div class="column">
                    <p class="text-xs"><strong>{{ $t('alarm_center_menu.phone') }}:</strong> {{ selectedAlarm.phone }}</p>
                    <p class="text-xs"><strong>{{ $t('alarm_center_menu.carrier') }}:</strong> {{ selectedAlarm.personal_alarm.name || ''  }}</p>
                    <p class="text-xs"><strong>{{ $t('alarm_center_menu.occupation') }}:</strong> {{ selectedAlarm.business_description || ''  }}</p>
                  </div>
                  <div class="column">
                    <p class="text-xs"><strong>{{ $t('alarm_center_menu.authorization_password') }}:</strong> {{ selectedAlarm.alarm_password || ''  }}</p>
                    <p class="text-xs"><strong>{{ $t('alarm_center_menu.starts_from') }}:</strong> {{ selectedAlarm.from_address.city || '' }}</p>
                    <p class="text-xs"><strong>{{ $t('alarm_center_menu.risk_profile') }}:</strong> {{ riskLabel(selectedAlarm.risk_profile) }}</p>
                  </div>
                </div>
                <hr class="custom-line" v-if="selectedAlarm.presentTagName">
                <div v-if="selectedAlarm.presentTagName" class="flex items-center gap-1">
                  <BaseIcon icon="house-user" class="ml-1" /><p class="text-xs"><strong>{{ $t('alarm_center_menu.tag_beacon') }}:</strong> {{ selectedAlarm.presentTagName || ''  }}</p>
                </div>
                <hr class="custom-line">
                <div v-if="selectedAlarm.activeVisit && selectedAlarm.currentVisit">
                  <p class="text-sm"><strong>{{ $t('alarm_center_menu.active_visit') }}</strong></p>
                </div>
                <div v-if="selectedAlarm.activeVisit && selectedAlarm.currentVisit" class="flex gap-2">
                  <div class="column">
                    <p class="text-xs"><strong>{{ $t('alarm_center_menu.address') }}:</strong> {{ selectedAlarm.currentVisit.address || ''  }}</p>
                    <p class="text-xs"><strong>{{ selectedAlarm.inventory_number != null && selectedAlarm.inventory_number.toString().trim() !== '' && !isNaN(Number(selectedAlarm.inventory_number)) ? $t('alarm_center_menu.name_and_phone'):$t('alarm_center_menu.floor')}}:</strong> {{ selectedAlarm.currentVisit.floor || ''  }}</p>
                    <p class="text-xs"><strong>{{ selectedAlarm.inventory_number != null && selectedAlarm.inventory_number.trim() !== '' ? $t('alarm_center_menu.reporting'):$t('alarm_center_menu.visit_host')}}:</strong> {{ selectedAlarm.currentVisit.host || ''  }}</p>
                  </div>
                  <div class="column">
                    <p class="text-xs"><strong>{{ $t('alarm_center_menu.start_time') }}:</strong> {{ selectedAlarm.currentVisit.startTime || ''  }}</p>
                    <p class="text-xs"><strong>{{ $t('alarm_center_menu.end_time') }}:</strong> {{ selectedAlarm.currentVisit.endTime || ''  }}</p>
                    <p class="text-xs"><strong>{{ selectedAlarm.inventory_number != null && selectedAlarm.inventory_number.trim() !== '' ? $t('alarm_center_menu.other_info'):$t('alarm_center_menu.visitor')}}:</strong> {{ selectedAlarm.currentVisit.visitor || ''  }}</p>
                  </div>
                </div>
                <hr class="custom-line" v-if="selectedAlarm.activeVisit && selectedAlarm.currentVisit">
                <div v-if="selectedAlarm.securappVisit">
                  <p class="text-sm"><strong>{{ $t('alarm_center_menu.active_visit') }}</strong></p>
                  <div class="flex gap-2">
                    <div class="column">
                      <p class="text-xs"><strong>{{ $t('alarm_center_menu.address') }}:</strong> {{ selectedAlarm.securappVisit.address }}</p>
                      <p class="text-xs"><strong>{{ $t('alarm_center_menu.contact_person') }}:</strong> {{ selectedAlarm.securappVisit.contactPerson }}</p>
                      <p class="text-xs"><strong>{{ $t('alarm_center_menu.door_code') }}:</strong> {{ selectedAlarm.securappVisit.doorCode }}</p>
                    </div>
                    <div class="column">
                      <p class="text-xs"><strong>{{ $t('alarm_center_menu.start_time') }}:</strong> {{ selectedAlarm.securappVisit.startTime }}</p>
                      <p class="text-xs"><strong>{{ $t('alarm_center_menu.end_time') }}:</strong> {{ selectedAlarm.securappVisit.endTime }}</p>
                      <p class="text-xs"><strong>{{ $t('alarm_center_menu.other_info') }}:</strong> {{ selectedAlarm.securappVisit.notes }}</p>
                    </div>
                  </div>
                  <hr class="custom-line">
                </div>
                <div v-if="!hasValidInstructions">
                  <p class="text-sm"><strong>{{ $t('alarm_center_menu.instructions') }}</strong></p>
                  <p class="text-xs">{{ $t('alarm_center_menu.primary_action') }}:<br></p>
                  <p class="text-xs">
                      {{ $t('alarm_center_menu.default_instruction_text') }}
                  </p>
                  <br>
                  <p class="text-xs">{{ $t('alarm_center_menu.action_on_alarm') }}<br></p>
                  <ol class="text-xs">
                    <li>
                        <strong>1. {{ $t('alarm_center_menu.monitoring') }}:</strong> {{ $t('alarm_center_menu.step1_monitoring_text') }}
                    </li>
                    <li>
                        <strong>2. {{ $t('alarm_center_menu.call_in') }}:</strong> {{ $t('alarm_center_menu.step2_call_in_text') }}
                    </li>
                    <li>
                        <strong>3. {{ $t('alarm_center_menu.emergency_alarm') }}:</strong> {{ $t('alarm_center_menu.step3_emergency_text') }}
                    </li>
                    <li>
                        <strong>4. {{ $t('alarm_center_menu.contact_person') }}:</strong> {{ $t('alarm_center_menu.step4_contact_person_text') }}
                    </li>
                    <li>
                        <strong>5. {{ $t('alarm_center_menu.recall') }}:</strong> {{ $t('alarm_center_menu.step5_recall_text') }} <i>{{ selectedAlarm.alarm_password || ''  }}</i>
                    </li>
                  </ol>
                </div>
                <div v-if="selectedAlarm.instructions && selectedAlarm.instructions !== 'Ingen instruktion'">
                    <p class="text-xs" v-html="selectedAlarm.instructions"></p>
                </div>
                <br>
                <table class="w-full text-xs text-left rtl:text-right text-gray-500 dark:text-gray-400">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{{ $t('alarm_center_menu.name') }}</th>
                      <th>{{ $t('alarm_center_menu.function') }}</th>
                      <th>{{ $t('alarm_center_menu.phone') }}</th>
                      <th>{{ $t('alarm_center_menu.weekday') }}</th>
                      <th>{{ $t('alarm_center_menu.saturday') }}</th>
                      <th>{{ $t('alarm_center_menu.sunday') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(person, index) in selectedAlarm.contact_persons" :key="index" v-if="person.name">
                      <td><b>{{ index + 1 }}</b></td>
                      <td>{{ person.name || 'N/A' }}</td>
                      <td>{{ person.function || 'N/A' }}</td>
                      <td>{{ person.phone || 'N/A' }}</td>
                      <td>{{ person.weekday || 'N/A' }}</td>
                      <td>{{ person.saturday || 'N/A' }}</td>
                      <td>{{ person.sunday || 'N/A' }}</td>
                    </tr>
                  </tbody>
                </table>
                <hr class="custom-line">
                  <div class="column">
                    <p class="text-xs"><strong>{{ $t('alarm_center_menu.security_company') }}:</strong> {{ selectedAlarm.external_contractor.name || ''  }}</p>
                  </div>
                  <div class="column">
                    <p class="text-xs"><strong>{{ $t('alarm_center_menu.security_company_phone') }}:</strong> {{ selectedAlarm.external_contractor.phone || ''  }}</p>
                  </div>
                <hr class="custom-line">
                <div class="button_container pb-2">
                  <div class="mt-2" v-if="selectedAlarm.pushButton && selectedAlarm.acknowledge === false && !selectedAlarm.initTestAlarm">
                    <button class="btn-red" v-on:click.stop="confirmAcknowledgeAlarmPrompt(selectedAlarm.alarm_id, selectedAlarm.alarm_name)">{{ $t('alarm_center_menu.acknowledge_alarm') }}</button>
                  </div>
      
                  <div class="mt-2 flex flex-row items-start gap-4" v-if="selectedAlarm.acknowledge && !selectedAlarm.initTestAlarm">
                    <button class="btn-secondary" v-on:click.stop="copyLink(selectedAlarm.imei_number)">
                      {{ $t('alarm_center_menu.copy_map_link') }}
                    </button>
                    
                    <button
                      v-bind:class="selectedAlarm.tracking_active ? 'btn-red' : 'btn-green'"
                      v-on:click.stop="trackAlarm(selectedAlarm, selectedAlarm.alarm_index)"
                      v-if="(selectedAlarm.pushButton || selectedAlarm.geofenceExit || (selectedAlarm.speed > 5 && selectedAlarm.lastSeenTime < 1000 * 60 * 30) || selectedAlarm.tracking_active) && selectedAlarm.acknowledge">
                      <span v-if="selectedAlarm.tracking_active">{{ $t("secur_track.disable_tracking") }}</span>
                      <span v-else>{{ $t("secur_track.enable_tracking") }}</span>
                    </button>
                    
                    <button class="btn-blue" v-on:click.stop="confirmTestAlarmPrompt(selectedAlarm)">
                      {{ $t("secur_track.test_alarm_confirmed") }}
                    </button>

                    <button class="btn-orange" v-on:click.stop="confirmResolveAlarmPrompt(selectedAlarm)">
                      {{ $t('alarm_center_menu.resolve_alarm') }}
                    </button>
                  </div>
    
                  <div class="mt-2" v-if="selectedAlarm.initTestAlarm && selectedAlarm.testAlarmReceived && !selectedAlarm.testConfirmed">
                    <button class="btn-blue" v-on:click.stop="confirmTestAlarmPrompt(selectedAlarm, selectedAlarm.alarm_index)">
                      {{ $t("secur_track.test_alarm_confirmed") }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="flex-grow p-2">
              <div class="flex items-center justify-center w-full">
                <div class="text-xs mt-36">{{ $t('alarm_center_menu.no_unit_selected') }}</div>
              </div>
            </div>
          </div>
      </div>
    </div>
    </div>
    <div class="alarmview">
       <!-- List of all alarms connected to alarm central -->
      <div class="bg-white alarm-container w-3/5 shadow-md">
        <div class="alarm-list-wrapper" v-bind:class="{ search: search_active }" v-if="gps_alarms">
            <div class="bg-gray-200 font-bold flex flex-col sticky-header">
              <div class="flex flex-row items-center">
                <div class="text-sm w-4/5 text-primary-700 pl-2">{{ $t('alarm_center_menu.alarm_list') }}</div>
                <div class="text-sm w-1/5 text-primary-700">
                  <AlarmSearch @queryUpdated="handleSearch" class="py-2 text-sm" v-if="!showOnlyEvent"/>
                </div>
              </div>
              <div class="text-center border-b border-secondary-400" v-if="gps_alarms && gps_alarms.length == 0">
                <div class="py-3 w-full">{{ $t("no_alarm_found") }}</div>
              </div>
              <div class="flex flex-row">
                <div class="text-xs w-1/5 text-primary-700 pl-2">{{ $t('alarm_center_menu.customer_name') }}</div>
                <div class="text-xs w-1/5 text-primary-700">{{ $t('alarm_center_menu.unit_name') }}</div>
                <div class="text-xs w-1/5 text-primary-700">{{ $t('alarm_center_menu.alarm_name') }}</div>
                <div class="text-xs w-1/5 text-primary-700">{{ $t('alarm_center_menu.event_type') }}</div>
                <div class="text-xs w-1/5 text-primary-700">{{ $t('alarm_center_menu.phone') }}</div>
                <div class="text-xs w-1/5 text-primary-700">{{ $t('alarm_center_menu.imei_number') }}</div>
              </div>
            </div>

            <div v-for="(a, index) in gps_alarms_filtered" :key="index" :class="['hover:bg-primary-50', index % 2 === 0 ? 'bg-white' : 'bg-gray-100']">
              <div @click="focusAlarm(a.imei_number)" class="cursor-pointer border-b border-secondary-300 flex flex-row items-center" 
              v-bind:class="{
                'bg-red-500 animate-pulse hover:bg-red-500': a.pushButton,
                'bg-blue-500': !a.pushButton && (a.highlight || a.selectedAlarmInList),
              }">
                <div
                  class="w-1/5 min-w-0 flex items-center pl-2 pr-2"
                  :class="{'text-white': a.pushButton || a.highlight || a.selectedAlarmInList}"
                >
                  <span class="truncate block text-sm font-bold">
                    {{ a.customer_name }}
                  </span>
                </div>

                <div class="text-sm mt-1 mb-1 w-1/5 flex items-center" v-bind:class="{'text-white': a.pushButton || a.highlight || a.selectedAlarmInList}">{{ a.unit_name }}</div>
                <div class="text-sm mt-1 mb-1 w-1/5 flex items-center" v-bind:class="{'text-white': a.pushButton || a.highlight || a.selectedAlarmInList}">
                  <span class="item text-xxs">
                    <span v-if="new RegExp('^SRT').test(a.type)" class="last-seen mr-1" v-bind:class="{ inactive: a.lastSeenTime > 1000 * 60 * 60 }">⬤</span>
                    <span v-if="new RegExp('^TWIG').test(a.type)" class="last-seen mr-1" v-bind:class="{ inactive: a.lastSeenTime > 1000 * 60 * 60 * 2.5 }">⬤</span>
                    <span v-if="new RegExp('^EMERIT').test(a.type)" class="last-seen mr-1" v-bind:class="{ inactive: a.lastSeenTime > 1000 * 60 * 60 }">⬤</span>
                    <span v-if="new RegExp('^TELTONIKA').test(a.type)" class="last-seen mr-1" v-bind:class="{ inactive: a.lastSeenTime > 1000 * 60 * 60 }">⬤</span>
                  </span>
                  {{ a.alarm_name }}
                </div>
                <div class="text-sm mt-1 mb-1 w-1/5 flex items-center" v-bind:class="{'text-white': a.pushButton || a.highlight || a.selectedAlarmInList}">
                  <span v-if="a.pushButton">{{ getEventType(a.imei_number) }}</span>
                </div>
                <div class="text-sm mt-1 mb-1 w-1/5 flex items-center" v-bind:class="{'text-white': a.pushButton || a.highlight || a.selectedAlarmInList}">{{ a.phone }}</div>
                <div class="text-sm mt-1 mb-1 w-1/5 flex items-center" v-bind:class="{'text-white': a.pushButton || a.highlight || a.selectedAlarmInList}">{{ a.imei_number }}</div>
              </div>              
            </div>
          </div>
      </div>

      <!-- List of acknowledged alarms -->
      <div class="bg-white alarm-container w-1/5 shadow-md">
        <div class="table-header bg-gray-200 font-bold flex flex-row border-b border-secondary-300 sticky-header">
          <div class="text-sm text-primary-700 ml-2">{{ $t('alarm_center_menu.acknowledged_alarms') }}</div>
        </div>
        <div v-for="(a, index) in getAcknowledgedAlarms" :key="index" :class="['hover:bg-green-100', index % 2 === 0 ? 'bg-white' : 'bg-gray-100']">
          <div @click="focusAlarm(a.imei_number)" class="cursor-pointer border-b border-secondary-300 flex justify-between items-center p-2" :class="{'bg-green-500 hover:bg-green-400': a.pushButton}">

            <div class="text-sm w-2/4 text-left text-white">{{ a.alarm_name }}</div>
            <div class="text-sm w-1/4 text-center text-white">{{ a.phone }}</div>
            <div class="text-sm w-1/4 text-right text-white">{{ a.formattedPushBTimestamp }}</div>
          </div>
        </div>
      </div>

      <!-- List of test alarms -->
      <div class="bg-white alarm-container w-1/5 shadow-md">
        <div class="table-header bg-gray-200 font-bold flex flex-row border-b border-secondary-300 sticky-header">
          <div class="text-sm w-1/5 text-primary-700 ml-2">{{ $t('alarm_center_menu.test_alarms') }}</div>
        </div>
        <div v-for="(a, index) in provlarmList" :key="a.id" :class="[index % 2 === 0 ? 'bg-white' : 'bg-gray-100']">
          <div @click="focusAlarm(a.imei_number)" 
              class="cursor-pointer border-b border-secondary-300 flex flex-row items-center" 
              v-bind:class="{ 
                'hover:bg-primary-100': a.initTestAlarm && !a.testAlarmReceived,
                'bg-red-500 hover:bg-red-500 triggered': a.testAlarmReceived,
                'bg-green-500 hover:bg-green-400': a.testConfirmed
              }">
            <div class="text-sm mt-1 mb-1 ml-2 w-3/5 flex items-center" v-bind:class="{'text-white': a.testConfirmed || a.testAlarmReceived}">{{ a.alarm_name }}</div>
            <div class="text-sm mt-1 mb-1 w-1/5 flex items-center" v-bind:class="{'text-white': a.testConfirmed || a.testAlarmReceived}">{{ a.phone }}</div>
          </div>
        </div>
      </div>

    </div>
    <!-- Jitsi iFrame container: visibility:hidden keeps it fully rendered in the DOM
         with real dimensions (400x300). Required for browser to allow audio OUTPUT
         (remote participants). A 1px or off-screen iFrame blocks remote audio playback. -->
    <div id="jitsi-api-container" style="position:fixed;left:-9999px;top:-9999px;width:400px;height:300px;visibility:hidden;"></div>
    <AlarmGPSInfo :alarmInfo="infoSRT326" />
  </div>
</template>

<script>
import { io } from "socket.io-client";
import AlarmGPSInfo from "@/components/modal/alarm_gps_info";
import { gmapApi } from "vue2-google-maps";
import "@googlemaps/markerclustererplus";
import AlarmSearch from "@/components/securetrack/alarm_search_lc";
import { riskProfileLabel } from "@/utils/alarmEnums";
require('moment-timezone/data/packed/latest');

export default {
  name: "AlarmCenterTrack",
  title() {
    return this.$t('page_titles.securtrack');
  },
  components: {
    AlarmGPSInfo,
    AlarmSearch,
  },
  props: {
    customerId: {
      type: String,
    },
  },
  data() { 
    return {
      socket: null,
      isMobile: false,
      mobileMenuActive: false,
      mapFullScreenControl: true,
      user: this.$store.state.user,
      alarm_center_id: this.$route.params.id,
      alarm_id: this.$route.params.alarm_id,
      firstLoad: true,
      alarm_center: null,
      customers: null,
      units: null,
      alarms: null,
      gps_alarms: [],
      //gps_alarms_filtered: null,
      track_alarm: [],
      zoom: this.$store.state.mapZoom,
      center: this.$store.state.mapCenter,
      markers: [],
      markers_filtered: [],
      refreshIntervalRef: null,
      refreshActiveIntervalRef: null,
      refreshInterval: 15000, // every 15sec
      infoWindowPos: null,
      infoWinOpen: false,
      currentMidx: null,
      infoOptions: { content: "", pixelOffset: { width: 0, height: -35 } },
      alarmCircles: [],
      alarmCircles_filtered: [],
      push_button_paths: [],
      push_button_paths_filtered: [],
      track_alarm_paths: [],
      track_alarm_paths_filtered: [],
      gps: [], 
      search_text: "",
      search_active: false,
      infoSRT326: null,
      searchQuery: "",
      selectedAlarm: null,
      showTestInput: false,
      inputPhone: '', 
      inputPassword: '',
      showAllAlarms: true,
      testConfirmed: false,
      testAlarmReceived: false,
      soundOn: true,
      alarmAudio: null,
      alarmPrepared: false,
      //showOnlyEvent: false,
      paths: { push_button: [], tracking: [] },
      acknowledgedAlarms: [],
      activeAlarms: [],
      highlightedAlarmImei: null,
      path_data: [],
      path_data2: [],
      activesocketEvents: [],
      // Inline voice room state (EMERIT e-B10)
      voiceCallStatus: 'idle',    // idle | connecting | active | ended | error
      voiceErrorMsg: null,
      micMuted: false,
      speakerMuted: false,
      participantCount: 0,
      jitsiApi: null,
      voiceCallAlarmId: null,
      callDurationSeconds: 0,
      callDurationTimer: null,
    };
  },

  watch: {
    selectedAlarm(newVal, oldVal) {
      if (newVal === oldVal) return;
      // If a call is active/connecting, check whether we're returning to that alarm
      if (this.voiceCallAlarmId && (this.voiceCallStatus === 'active' || this.voiceCallStatus === 'connecting')) {
        if (newVal && newVal.alarm_id === this.voiceCallAlarmId) {
          return;
        }
        // Switching to a different alarm — keep the call running in background
        return;
      }

      // No active call — reset voice panel for the newly selected alarm
      this.voiceCallStatus = 'idle';
      this.voiceErrorMsg = null;
    },
  },

  methods: {
    riskLabel(value) {
      return riskProfileLabel((k) => this.$t(k), value);
    },
    // ─── Inline Voice Room Methods (EMERIT e-B10) ─────────────────────────────
    async joinVoiceRoomInline() {
      try {
        this.voiceCallStatus = 'connecting';
        this.voiceErrorMsg = null;

        // Trigger the browser's native mic permission dialog.
        // On denial the browser shows its own blocked indicator in the URL bar.
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach(t => t.stop());
        } catch (_permError) {
          this.voiceCallStatus = 'idle';
          return;
        }

        const response = await this.axios.get(
          `${process.env.VUE_APP_SERVER_URL}/alarms/${this.selectedAlarm.alarm_id}/operator-voice-token`
        );
        const { domain, roomName, jwt } = response.data.data;
        this.voiceCallAlarmId = this.selectedAlarm.alarm_id;

        await this.loadJitsiScript(domain);

        const container = document.getElementById('jitsi-api-container');
        const api = new window.JitsiMeetExternalAPI(domain, {
          roomName,
          jwt,
          parentNode: container,
          width: 400,
          height: 300,
          configOverwrite: {
            startAudioOnly: true,
            prejoinConfig: { enabled: false },
            startWithAudioMuted: false,
          },
          interfaceConfigOverwrite: {
            TOOLBAR_BUTTONS: [],
            SHOW_JITSI_WATERMARK: false,
            SHOW_WATERMARK_FOR_GUESTS: false,
            SHOW_POWERED_BY: false,
            filmStripOnly: false,
          },
        });

        this.jitsiApi = api;

        api.on('videoConferenceJoined', () => {
          this.voiceCallStatus = 'active';
          this.micMuted = false;
          this.participantCount = api.getParticipantsInfo().length;
          this.startCallTimer();
        });

        api.on('audioMuteStatusChanged', ({ muted }) => {
          this.micMuted = muted;
        });

        api.on('participantJoined', () => {
          this.participantCount = api.getParticipantsInfo().length;
        });

        api.on('participantLeft', () => {
          this.participantCount = api.getParticipantsInfo().length;
        });

        api.on('videoConferenceLeft', () => {
          this.leaveVoiceRoomInline();
        });

      } catch (error) {
        this.voiceCallStatus = 'error';
        this.voiceErrorMsg = error?.response?.data?.message || 'Could not retrieve voice token.';
      }
    },

    loadJitsiScript(domain) {
      return new Promise((resolve, reject) => {
        if (window.JitsiMeetExternalAPI) { resolve(); return; }
        const script = document.createElement('script');
        script.src = `https://${domain}/external_api.js`;
        script.onload = resolve;
        script.onerror = () => reject(new Error('Failed to load Jitsi External API'));
        document.head.appendChild(script);
      });
    },

    toggleMic() {
      if (this.jitsiApi) {
        this.jitsiApi.executeCommand('toggleAudio');
      }
    },

    hangupVoice() {
      if (this.jitsiApi) {
        this.jitsiApi.executeCommand('hangup');
      }
      this.leaveVoiceRoomInline();
    },

    leaveVoiceRoomInline() {
      if (this.jitsiApi) {
        this.jitsiApi.dispose();
        this.jitsiApi = null;
      }
      this.stopCallTimer();
      this.voiceCallStatus = 'ended';
      this.micMuted = false;
      this.speakerMuted = false;
      this.participantCount = 0;
      this.voiceCallAlarmId = null;
      this.callDurationSeconds = 0;
    },

    toggleSpeaker() {
      this.speakerMuted = !this.speakerMuted;
      const container = document.getElementById('jitsi-api-container');
      const iframe = container ? container.querySelector('iframe') : null;
      if (iframe) iframe.muted = this.speakerMuted;
    },

    startCallTimer() {
      this.callDurationSeconds = 0;
      this.callDurationTimer = setInterval(() => {
        this.callDurationSeconds++;
      }, 1000);
    },

    stopCallTimer() {
      if (this.callDurationTimer) {
        clearInterval(this.callDurationTimer);
        this.callDurationTimer = null;
      }
    },
    // ─── End Inline Voice Room Methods ────────────────────────────────────────

    async refreshGPSData() {
      this.refreshIntervalRef = setInterval(async () => {
        try {
          await this.getAlarmCenterGPSAlarms();
        } catch (error) {
          this.handleError("Error when fetching GPS-data:", error);
        }
      }, this.refreshInterval);

      // this.refreshActiveIntervalRef = setInterval(async () => {
      //   try {
      //     await this.getAlarmCenterGPSAlarms();//this.getActiveAlarm();
      //   } catch (error) {
      //     this.handleError("Error when fetching GPS-data:", error);
      //   }
      // }, 10000);

    },
    // mergeByIMEI(existingArray, newArray) {
    //   const merged = [...(existingArray || [])];
    //   newArray.forEach(newItem => {
    //     const index = merged.findIndex(existingItem =>
    //       existingItem.imei_number === newItem.imei_number
    //     );
    //     if (index > -1) {
    //       // Preserve highlight and pushButton if set locally
    //       merged[index] = {
    //         ...merged[index],
    //         ...newItem,
    //         highlight: merged[index].highlight || newItem.highlight,
    //         pushButton: merged[index].pushButton || newItem.pushButton,
    //       };
    //     } else {
    //       merged.push(newItem);
    //     }
    //   });
    //   return merged;
    // },
    // getActiveAlarm() {

    //   if(this.activeAlarms.length > 0 && this.gps_alarms.length > 0){
    //     this.axios
    //     .post(`${process.env.VUE_APP_SERVER_URL}/alarm-center/${this.alarm_center_id}/gps/alarms`,  {imei_numbers: this.activeAlarms})
    //     .then((response) => {

    //       this.gps_alarms = this.mergeByIMEI(this.gps_alarms, response.data.gps_alarms);
    //       this.markers = this.mergeByIMEI(this.markers, response.data.markers);
    //       this.alarm_center = response.data.alarm_center;
    //       this.alarmCircles = this.mergeByIMEI(this.alarmCircles, response.data.alarmCircles);
    //       this.prepareAlarmList();
    //       const tracking_active_count = this.gps_alarms.reduce((acc, item) => acc + (item.tracking_active === true ? 1 : 0), 0);
    //       //console.log("tracking_active_count: ", tracking_active_count);
    //       if(tracking_active_count > 0){
    //         this.track_alarm_paths_filtered = this.track_alarm_paths
    //       }
    //     }).catch((error) => {
    //       this.handleError(error, "Fel när plats hämtades");
    //     });
    //   }else{
    //     this.stopAlarmSound();
    //   }
    // },
    getAlarmCenterGPSAlarms() {
      this.axios
        .post(`${process.env.VUE_APP_SERVER_URL}/alarm-center/${this.alarm_center_id}/gps/alarms`,  {imei_numbers: []})
        .then((response) => {

          //console.log("alarms :", response.data.alarms);
          // Directly use pre-processed data from backend
          this.alarm_center = response.data.alarm_center;
          this.gps_alarms = response.data.gps_alarms;
          //this.gps_alarms_filtered = response.data.gps_alarms;
          this.markers = response.data.markers;
          this.alarmCircles = response.data.alarmCircles;
          //this.push_button_paths = response.data.paths.push_button;
          //this.track_alarm_paths = response.data.paths.tracking;
          this.track_alarm = response.data.track_alarm
          this.gps = response.data.gps

          
          this.prepareAlarmList();
          // Client-side only UI state
          this.alarmCircles_filtered = [];
          this.push_button_paths_filtered = [];
          this.track_alarm_paths_filtered = [];
          //this.markers_filtered = [];

          // Sync selectedAlarm.pushButton from refreshed gps_alarms so the
          // voice room panel appears after a push_button event without re-selecting
          if (this.selectedAlarm) {
            const fresh = this.gps_alarms.find(a => a.imei_number === this.selectedAlarm.imei_number);
            if (fresh) {
              this.selectedAlarm.pushButton = fresh.pushButton;
              this.selectedAlarm.acknowledge = fresh.acknowledge;
            }
          }

          // Initial load handling
          if (this.firstLoad) {
            this.handleInitialMapView();
            this.firstLoad = false;
          }

          // UI feedback
          if (document.hasFocus()) {
            this.handleSuccess(this.$t('success_messages.location_updated'), "top-left", 1000);
          }

        })
        .catch((error) => {
          this.handleError(error, "Fel när plats hämtades");
        });
    },
    prepareAlarmList() {
      this.activeAlarms = [];
      let alarmCount = 0;
      const now = Date.now();

      this.gps_alarms.forEach(el => {
        const imei = el.imei_number;
        if (el.initTestAlarm) {
          this.showTriggredAlarm(el, true);
        }
      
        const lastTestAlarmTime = el.last_test_alarm ? new Date(el.last_test_alarm).getTime() : 0;
        const acknowledgeTime = el.lastAcknowledgeTimeStamp ? new Date(el.lastAcknowledgeTimeStamp).getTime() : 0;
        
        // if(el.imei_number == '359622093315999'){
        //   console.log("el.pushButton: ", el.pushButton, "acknowledgeTime: ", this.moment(acknowledgeTime).format("YYYY-MM-DD HH:mm:ss")," lastPushButtonTimestamp: ", this.moment(el.lastPushButtonTimestamp).format("YYYY-MM-DD HH:mm:ss"));
        // }

        let diffInMinutes = null;
        const newlyAcknowledged = Number.isFinite(diffInMinutes) && diffInMinutes < 30;

        // Push B event logic (existing code)
        el.pushButton = false;  
        if (el.lastPushButtonTimestamp && !isNaN(new Date(el.lastPushButtonTimestamp).getTime())) {
          if (el.lastPushButtonTimestamp && new Date().getTime() - el.lastPushButtonTimestamp < 1800000) {
            let pushButtonTime = new Date(el.lastPushButtonTimestamp);
            let initTestTime = new Date(el.lastInitTestAlarmTimeStamp);
            let lastTestAlarmTime = new Date(el.last_test_alarm);

            if ((Date.now() - initTestTime) <= 10 * 60 * 1000 && !el.testConfirmed && initTestTime < pushButtonTime) {
              el.testAlarmReceived = true;
              el.pushButton = false;

            } else if (((pushButtonTime - initTestTime >= 10 * 60 * 1000) || el.testConfirmed) && (pushButtonTime > lastTestAlarmTime)) {
              el.pushButton = true;
            }
            

            if (el.type != "SRT326") {
              this.alarmCircles.push({
                position: { lat: el.gps_pos.lat, lng: el.gps_pos.lng },
                imei_number: el.imei_number,
                alarmId: el.alarm_id,
                options: {
                  fillColor: "#FF0000",
                  fillOpacity: 0.1,
                  strokeColor: "#FF0000",
                  strokeOpacity: 0.2,
                  strokeWeight: 2,
                  radius: 50,
                },
              });
            }


            // Push B event - PRESERVE current pushButton unless we can confidently recompute
            let pushFlag = el.pushButton === true; // start from current UI state (preserve socket triggers)
            const ts = el.lastPushButtonTimestamp;

            if (ts && !isNaN(new Date(ts).getTime())) {
              const pushButtonTime = new Date(ts);
              const within30m = (Date.now() - pushButtonTime.getTime()) < 1800000;

              if (within30m) {
                const initTestTime = el.lastInitTestAlarmTimeStamp ? new Date(el.lastInitTestAlarmTimeStamp) : null;
                const lastTestAlarmTime = el.last_test_alarm ? new Date(el.last_test_alarm) : null;

                // Test alarm logic
                if (initTestTime && (Date.now() - initTestTime.getTime()) <= 10 * 60 * 1000 && !el.testConfirmed && (!pushButtonTime || initTestTime < pushButtonTime)) {
                  el.testAlarmReceived = true;
                  pushFlag = false;
                } else if ((!initTestTime || (pushButtonTime - initTestTime >= 10 * 60 * 1000) || el.testConfirmed) && (!lastTestAlarmTime || pushButtonTime > lastTestAlarmTime)) {
                  pushFlag = true;
                }
              } else {
                // older than 30 mins -> not red
                pushFlag = false;
              }
            }
            // Apply final decision
            el.pushButton = pushFlag;

            // Update activeAlarms based on final pushButton state
            if (el.pushButton && !this.activeAlarms.includes(imei)) {
              this.activeAlarms.push(imei);
              alarmCount++;
            }

            if (el.type != "SRT326" && el.pushButton) {
              this.alarmCircles.push({
                position: { lat: el.gps_pos.lat, lng: el.gps_pos.lng },
                imei_number: el.imei_number,
                alarmId: el.alarm_id,
                options: {
                  fillColor: "#FF0000",
                  fillOpacity: 0.1,
                  strokeColor: "#FF0000",
                  strokeOpacity: 0.2,
                  strokeWeight: 2,
                  radius: 50,
                },
              });
            }


            let diffInMinutes = null;
            el.acknowledge = false;
            if (el.lastAcknowledgeTimeStamp && el.lastPushButtonTimestamp) {
              const pushButtonTime = new Date(el.lastPushButtonTimestamp);
              const acknowledgeTime = new Date(el.lastAcknowledgeTimeStamp);
              const lastTestAlarmTime = el.last_test_alarm ? new Date(el.last_test_alarm) : null;

              if (acknowledgeTime > pushButtonTime) {
                el.acknowledge = true;

                const currentTime = new Date();
                diffInMinutes = (currentTime - acknowledgeTime) / (1000 * 60);
                // If acknowledge timestamp is more than 30 mins old, set to false and alarm will transfer to normal alarm list.
                if (diffInMinutes > 30) {
                  el.acknowledge = false;
                  el.pushButton = false;
                  this.removeAlarmCircle(el.imei_number);
                }
                if (lastTestAlarmTime && lastTestAlarmTime > acknowledgeTime) {
                  el.acknowledge = false;
                  el.pushButton = false;
                }
              } 
            }
          }
        }
        
        // Calculate pushWithin30 AFTER pushButton logic is complete
        const lastPush = this.moment(el.lastPushButtonTimestamp, true);
        const isWithin30Min = lastPush.isValid() && 
                      lastPush.isAfter(this.moment().subtract(30, 'minutes')) &&
                      lastPush.isSameOrBefore(this.moment());
        const pushWithin30 = el.pushButton && el.hasSecurpos && isWithin30Min;

        // SecurApp temporary visit — sent by the app with the alarm event and
        // persisted on the alarm document (securapp_active_visit).
        // Fields are camelCase; startTime/endTime are local wall-clock strings
        // without timezone, so format via string manipulation (no UTC parsing).
        el.securappVisit = null;
        const sav = el.securapp_active_visit;
        if (sav && (el.pushButton || (sav.endTime && this.moment(sav.endTime, 'YYYY-MM-DDTHH:mm:ss').isSameOrAfter(this.moment())))) {
          el.securappVisit = {
            address: sav.address || '',
            contactPerson: sav.contactPerson || '',
            doorCode: sav.doorCode || '',
            notes: sav.notes || '',
            startTime: sav.startTime ? sav.startTime.replace('T', ' ').slice(0, 16) : '',
            endTime: sav.endTime ? sav.endTime.replace('T', ' ').slice(0, 16) : '',
          };
        }

        //SecurPos Handling
        if (this.selectedAlarm && imei === this.selectedAlarm.imei_number) {
          this.$set(this.selectedAlarm, 'presentTagName', el.presentTagName);
          this.$set(this.selectedAlarm, 'securappVisit', el.securappVisit);
              
          //console.log("pushWithin30: ", pushWithin30, "newlyAcknowledged: ", newlyAcknowledged," lastTestAlarmTime: ", lastTestAlarmTime);

          //testReceivedWithSecur
          if (pushWithin30 || (newlyAcknowledged && lastTestAlarmTime < acknowledgeTime)) {
            //console.log("inside securePost time: ", this.selectedAlarm.currentVisit);
            if (!this.selectedAlarm.currentVisit || !this.selectedAlarm?.activeVisit) {
              
              this.getSecurPosVisit(el.phone).then(response => {
                if (response.success) {
                  const visit = response.visit;
                  const startTime = new Date(visit.startTime);
                  const endTime = new Date(startTime.getTime() + visit.timeLength * 1000);

                  const currentVisit = {
                    address: visit.address.area || "",
                    floor: visit.address.floor || "",
                    host: visit.address.building || "",
                    visitor: visit.address.visitor || "",
                    startTime: this.moment(startTime).format("YYYY-MM-DD HH:mm"),
                    endTime: this.moment(endTime).format("YYYY-MM-DD HH:mm"),
                  };

                  this.$set(el, 'currentVisit', currentVisit);
                  this.$set(el, 'activeVisit', true);
                  this.$set(this.selectedAlarm, 'currentVisit', currentVisit);
                  this.$set(this.selectedAlarm, 'activeVisit', true);
                }
              }).catch(console.error);
              
            }
          }
        }

        // Push button active
        if (el.pushButton) {
          this.activeAlarms.push(imei);
          alarmCount++;
          this.showTriggredAlarm(el);
        } 

        //Alarm Tracking
        if (el.alarm_track_user) {
          el.alarm_track_user.forEach(track => {
            const trackTime = new Date(track.timestamp).getTime();
            if (now - trackTime < 1800000) {
              const trackAlarm = _.find(this.track_alarm, { _id: track.tracking_id, user_name: this.user.username });
              if (trackAlarm) {
                this.track_alarm_paths.push({
                  path: _.map(trackAlarm.gps_path, "position"),
                  alarmId: el.alarm_id,
                  imei_number: imei,
                  infoText: this.$t('alarm_center_messages.tracking', { name: el.alarm_name }),
                });
                el.tracking_active = true;
                el.tracking_id = track.tracking_id;
              }
            }
          });
        }
        
      });

      this.focusAlarm(this.highlightedAlarmImei);
      setTimeout(() => {
        const option = this.alarm_center?.alarm_presentation_options;

        // Only narrow to a single alarm if:
        // - an alarm is selected
        // - NOT showing all
        // - NOT in 'showOnlyEvent' mode
        if (this.selectedAlarm && !this.showAllAlarms && option !== 'showOnlyEvent') {
          this.applySingleAlarmFilters();
          } else {
            this.applyFullViewFilters();
          }
        }, 200);

      
      // Stop alarm if no active alarms
      if (alarmCount === 0) {
        this.stopAlarmSound();
      }
    },
    applyFullViewFilters() {
      const option = this.alarm_center?.alarm_presentation_options;

      if (option === 'showOnlyEvent') {
        // show ONLY active-event markers
        this.markers_filtered = this.computeMarkersForPresentation();

        // Keep circles/paths consistent with visible markers
        const visible = new Set(this.markers_filtered.map(m => String(m.imei_number ?? m.imei)));
        this.alarmCircles_filtered   = (this.alarmCircles || []).filter(c => visible.has(String(c.imei_number ?? c.imei)));
        this.push_button_paths_filtered = (this.push_button_paths || []).filter(p => visible.has(String(p.imei_number ?? p.imei)));
        // this.track_alarm_paths_filtered = (this.track_alarm_paths || []).filter(t => visible.has(String(t.imei_number ?? t.imei)));
      } else {
        // show ALL
        this.markers_filtered = this.markers || [];
        this.alarmCircles_filtered = this.alarmCircles || [];
        this.push_button_paths_filtered = this.push_button_paths || [];
        // this.track_alarm_paths_filtered = this.track_alarm_paths || [];
      }
    },
    applySingleAlarmFilters() {
      const option = this.alarm_center?.alarm_presentation_options;

      // In showOnlyEvent mode we don't narrow to one alarm; keep presentation view
      if (option === 'showOnlyEvent') {
        this.applyFullViewFilters();
        return;
      }

      if (this.selectedAlarm) {
        const filterBy = this.selectedAlarm.imei_number;
        this.markers_filtered = (this.markers || []).filter(m => m.imei_number === filterBy);
        this.alarmCircles_filtered = (this.alarmCircles || []).filter(c => c.imei_number === filterBy);
        this.push_button_paths_filtered = (this.push_button_paths || []).filter(p => p.imei_number === filterBy);
        // this.track_alarm_paths_filtered = (this.track_alarm_paths || []).filter(t => t.imei_number === filterBy);
      }
    },
    computeMarkersForPresentation() {
      const option = this.alarm_center?.alarm_presentation_options;
      if (!Array.isArray(this.markers) || !Array.isArray(this.gps_alarms)) return [];

      if (option === 'showOnlyEvent') {
        const activeImeiSet = new Set(
          this.gps_alarms
            .filter(a => a && a.pushButton === true && !a.testAlarmReceived && !a.initTestAlarm)
            .map(a => String(a.imei_number ?? a.imei).trim())
        );
        return (this.markers || []).filter(m =>
          activeImeiSet.has(String(m.imei_number ?? m.imei).trim())
        );
      }

      // default: show all
      return this.markers || [];
    },
    handleInitialMapView() {
      this.$nextTick(() => {
        if (this.alarm_id) {
          this.$nextTick().then(() => {
            const alarm = this.gps_alarms.find(a => a._id === this.alarm_id);
            if (alarm) this.focusAlarm(alarm.imei_number);
          });
        } 
      });
    },
    // alarmClick(marker) {

    //   this.toggleInfoWindow(marker, this.markers.indexOf(marker));
    //   let targetImei = marker.imei_number;
    //   this.highlightedAlarmImei = targetImei;
    //   const alarms = this.gps_alarms;
    //   for (let i = 0, len = alarms.length; i < len; i++) {
    //     const alarm = alarms[i];

    //     if(alarm.pushButton){
    //       return;
    //     }

    //     this.gps_alarms[i].selectedAlarmInList = false

    //     const isTarget = alarm.imei_number === targetImei;
    //     if(isTarget){
    //       this.$set(this.gps_alarms[i], 'highlight', isTarget);
    //     }else{
    //       this.gps_alarms[i].highlight = isTarget
    //     }
    //   }
    // },
    resetSelectedList(){
      for (let i = 0; i < this.gps_alarms.length; i++) {
        this.gps_alarms[i].highlight = false
        this.gps_alarms[i].selectedAlarmInList = false
      }
    },
    toggleInfoWindow(marker, idx, eventType) {
      //console.log("Toggle info window for marker:", marker, "at index:", idx, "eventType:", eventType);
      this.infoWindowPos = marker.position;
      this.infoOptions.content = marker.infoText;
      if (eventType === 'mouseover') {
        // Always open on mouseover
        this.infoWinOpen = true;
        this.currentMidx = idx;
        this.alarm_id = marker.alarmId;
      } 
    },
    closeInfoWindow() {
      //console.log("Closing info window: ", infoOptions);
      this.infoWinOpen = false;
      this.alarm_id = "";
      this.currentMidx = null;

      this.applyFullViewFilters();
      //this.highlightedAlarmImei = null;
      this.gps_alarms = this.gps_alarms.map(alarm => ({
        ...alarm,
        highlight: false
      }));
      setTimeout(()=>{
        this.resetSelectedList();
      },500)
    },
    showInfoWindow(event, infoText, show) {
      //console.log("showInfoWindow", event);
      if (show) {
        this.infoWindowPos = event.latLng;
        this.infoOptions.content = infoText;
        this.infoWinOpen = show;
      } else this.infoWinOpen = show;
    },
    focusAlarm(imei) {

      // Find the full alarm object
      const alarm = this.gps_alarms.find(a => a.imei_number === imei);
      
      if (alarm) {
        // Update filtered views
        this.markers_filtered = this.markers.filter(m => m.imei_number === imei);
        this.alarmCircles_filtered = this.alarmCircles.filter(c => c.imei_number === imei);
        this.push_button_paths_filtered = this.paths.push_button.filter(p => p.imei_number === imei);

        // Update map view
        setTimeout(() => {
          if (this.markers_filtered.length > 0) {
            this.$refs.map.$mapObject.panTo(this.markers_filtered[0].position);
            this.$refs.map.$mapObject.setZoom(18);
          }
        }, 500)

        // Set selected alarm with additional data
        this.selectedAlarm = { 
          ...alarm,
          formattedPushBTimestamp: alarm.lastPushButtonTimestamp ? 
            new Date(alarm.lastPushButtonTimestamp).toLocaleTimeString("sv-SE", {
              hour: "2-digit",
              minute: "2-digit",
            }) : "N/A"
        };

        //console.log("Selected alarm:", this.selectedAlarm);
        
        setTimeout(()=>{
          //console.log("markers_filtered:", this.markers_filtered);
          if(this.markers_filtered.length > 0){
            this.showInfoWindow(
              { latLng: this.markers_filtered[0].position },
              this.markers_filtered[0].infoText,
              true
            );
          }
        }, 100)

      } else {
        //console.log("Alarm not found with IMEI:", imei);
      }
      
    },
    showSRT326(alarm) {
      this.infoSRT326 = alarm;
      this.$modal.show("modal-info-srt326");
    },

    getGSMSignalPercent(g, type) {
      if (new RegExp(/^TELTONIKA/g).test(type)) {
        return g * 20 + "%";
      }
      {
        // conversion as per SRT documentation
        if (g <= 31) return Math.ceil(3.225 * g) + " %";
        else return "N/A";
      }
    },

    getUnit(id) {
      let u = _.find(this.units, { unit_id: id });
      if (u) return u;
      else return null;
    },

    getCustomerName(id) {
      let c = _.find(this.customers, { _id: id });
      if (c) return c.name;
      else return "";
    },

    async trackAlarm(gps) {

      //console.log("trackAlarm: ", gps);
      const alarm_index = this.gps_alarms.findIndex(item => item.imei_number === gps.imei_number); 
      try {
        if (!gps.tracking_active) {
          // start tracking
          let alarm = _.find(this.gps_alarms, { imei_number: gps.imei_number });
 
          let speed = parseFloat(gps.alarm_speed);
          if (alarm && new RegExp(/^SRT/g).test(alarm.type)) speed = (parseFloat(gps.alarm_speed) * 1.852).toFixed(2);

          let data = {
            position: gps.gps_pos,
            speed: speed,
          };

          let response = await this.axios.post(`${process.env.VUE_APP_SERVER_URL}/gps/track-alarm/start/${gps.imei_number}`, data);
          let track_alarm = response.data.track_alarm;
          let path = _.map(track_alarm.gps_path, "position");

          this.track_alarm_paths.push({
            path: path,
            imei_number: gps.imei_number,
            alarmId: gps.alarm_id,
            infoText: this.$t('alarm_center_messages.tracking', { name: gps.alarm_name }),
          });

          this.track_alarm_paths_filtered = this.track_alarm_paths

          this.$set(this.gps_alarms[alarm_index], 'tracking_active', true);
          this.$set(this.gps_alarms[alarm_index], 'tracking_id', track_alarm._id);
          this.$set(this.selectedAlarm, 'tracking_active', true);

          this.focusAlarm(gps.imei_number);
          this.handleSuccess(this.$t('alarm_center_messages.tracking_started', { name: gps.alarm_name }));
        } else {
          // stop tracking
          let data = {
            tracking_id: gps.tracking_id,
          };

          let response = await this.axios.post(`${process.env.VUE_APP_SERVER_URL}/gps/track-alarm/stop/${gps.imei_number}`, data);
          if (response.data.success) {
            this.track_alarm_paths = _.remove(this.track_alarm_paths, { imei_number: gps.imei_number });
            this.$set(this.gps_alarms[alarm_index], 'tracking_active', false);
            this.gps_alarms[alarm_index].tracking_id = null;
            this.handleSuccess(this.$t('alarm_center_messages.tracking_stopped', { name: gps.alarm_name }));
            this.$set(this.selectedAlarm, 'tracking_active', false);
          }
        }
      } catch (error) {
        this.handleError(error);
      }

    },

    confirmTestAlarmPrompt(alarm) {
      this.$modal.show("dialog", {
        title: this.$t('alarm_center_messages.are_you_sure'),
        text: `<p style="text-align: center;">${this.$t('alarm_center_messages.confirm_test_alarm', { name: alarm.alarm_name })}</p>`,
        buttons: [
          {
            title: `<div class="bg-accent-500 text-white text-sm font-sans py-2">${this.$t('alarm_center_messages.no')}</div>`,
            handler: () => {
              this.$modal.hide("dialog");
            },
          },
          {
            title: `<div class="bg-red-500 text-white text-sm font-sans py-2">${this.$t('alarm_center_messages.yes')}</div>`,
            handler: () => {
              this.confirmTestAlarm(alarm);
            },
          },
        ],
      });
    },

    async confirmTestAlarm(alarm) {
      try {
        let alarmIndex = this.gps_alarms.findIndex(el => el.imei_number === alarm.imei_number);
        // Do NOT set initTestAlarm here, let backend and polling handle it
        // this.$set(this.gps_alarms[alarmIndex], 'initTestAlarm', false);

        let response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/alarm/confirm-test-alarm/${alarm.alarm_id}`);

        this.$modal.hide("dialog");
        if (response.data.success) {
          // Only set testConfirmed and clear acknowledge/pushButton
          this.$set(this.gps_alarms[alarmIndex], 'testConfirmed', true);
          this.$set(this.gps_alarms[alarmIndex], 'acknowledge', false);
          this.$set(this.gps_alarms[alarmIndex], 'pushButton', false);

          this.alarmCircles = this.alarmCircles.filter(circle => circle.imei_number !== alarm.imei_number);

          if (this.selectedAlarm && this.selectedAlarm.imei_number === alarm.imei_number) {
            this.$set(this.selectedAlarm, 'testConfirmed', true);
            this.$set(this.selectedAlarm, 'acknowledge', false);
            this.$set(this.selectedAlarm, 'pushButton', false);
          }

          // Optionally: this.getAlarmCenterGPSAlarms(); // keep for backend sync
          this.handleSuccess(this.$t('alarm_center_messages.test_alarm_confirmed', { name: alarm.unit_name }));
        }
      } catch (error) {
        this.$modal.hide("dialog");
        this.handleError(error);
      }
    },

    confirmAcknowledgeAlarmPrompt(id ,name) {
      this.$modal.show("dialog", {
        title: this.$t('alarm_center_messages.acknowledge_alarm_title'),
        text: `<p style="text-align: center;">${this.$t('alarm_center_messages.acknowledge_alarm_text', { name })}</p>`,
        buttons: [
          {
            title: `<div class="bg-success-500 text-white text-sm font-sans py-2">${this.$t('alarm_center_messages.no')}</div>`,
            handler: () => {
              this.$modal.hide("dialog");
            },
          },
          {
            title: `<div class="bg-red-500 text-white text-sm font-sans py-2">${this.$t('alarm_center_messages.yes')}</div>`,
            handler: () => {
              // Call function to store confirm in DB. 
              this.confirmAcknowledgeAlarm(id, name);
            },
          },
        ],
      });
    },
    async confirmAcknowledgeAlarm(id, name) {

      let alarmId = id;
      let alarmIndex = this.gps_alarms.findIndex(el => el.alarm_id === id);

      try {
        /*
        Api call to store acknowledge timestamp in GPS collection. If success it stops the alarm
        and fetch alarm center alarms again to refresh data.
        */
        let response = await this.axios.post(`${process.env.VUE_APP_SERVER_URL}/alarm/acknowledge-alarm/${alarmId}`);
        this.$modal.hide("dialog");

        // if response is false, the alarm has already been acknowledged. Shows message is toaster.
        if (response.data.success) {
          
          this.$set(this.selectedAlarm, 'acknowledge', true);
          this.$set(this.gps_alarms[alarmIndex], 'acknowledge', true);
          //this.$set(this.gps_alarms[alarmIndex], 'pushButton', false);

          this.handleSuccess(this.$t('alarm_center_messages.alarm_acknowledged', { name }));
          setTimeout(() => {
            this.stopAlarmSound();
          }, 500)
          this.getAlarmCenterGPSAlarms(); //this.getActiveAlarm();
        }

        //get alarm from gps_alarms by the id and get the imei_number, 
        // then match with activesocketEvents using the IMEi and delete the event_type from the localStorage
        let alarm = this.gps_alarms.find(a => a.alarm_id === alarmId);
        if (!alarm) {
          //console.log("Alarm not found with ID:", alarmId);
          return;
        }
        
        //remove the event_type from the activesocketEvents for this alarm using alarm.imei_number
        this.activesocketEvents = localStorage.getItem('activesocketEvents')  ? JSON.parse(localStorage.getItem('activesocketEvents')) : [];
        this.activesocketEvents = this.activesocketEvents.filter(event => event.imei_number !== alarm.imei_number);
        localStorage.setItem('activesocketEvents', JSON.stringify(this.activesocketEvents));

      } catch (error) {
        this.$modal.hide("dialog");
        this.handleError(error);
      }
    },

    confirmResolveAlarmPrompt(alarm) {
      this.$modal.show("dialog", {
        title: this.$t('alarm_center_messages.resolve_alarm_title'),
        text: `<p style="text-align: center;">${this.$t('alarm_center_messages.resolve_alarm_text', { name: alarm.alarm_name })}</p>`,
        buttons: [
          {
            title: `<div class="bg-accent-500 text-white text-sm font-sans py-2">${this.$t('alarm_center_messages.no')}</div>`,
            handler: () => {
              this.$modal.hide("dialog");
            },
          },
          {
            title: `<div class="bg-red-500 text-white text-sm font-sans py-2">${this.$t('alarm_center_messages.yes')}</div>`,
            handler: () => {
              this.resolveAlarm(alarm);
            },
          },
        ],
      });
    },

    async resolveAlarm(alarm) {
      try {
        let alarmIndex = this.gps_alarms.findIndex(el => el.alarm_id === alarm.alarm_id);

        let response = await this.axios.post(`${process.env.VUE_APP_SERVER_URL}/alarm/resolve-alarm/${alarm.alarm_id}`);
        this.$modal.hide("dialog");

        if (response.data.success) {
          // If voice room is active for this alarm, hang up
          if (this.voiceCallAlarmId === alarm.alarm_id && this.jitsiApi) {
            this.leaveVoiceRoomInline();
          }

          // Reset alarm state locally
          if (alarmIndex > -1) {
            this.$set(this.gps_alarms[alarmIndex], 'pushButton', false);
            this.$set(this.gps_alarms[alarmIndex], 'acknowledge', false);
          }

          if (this.selectedAlarm && this.selectedAlarm.alarm_id === alarm.alarm_id) {
            this.$set(this.selectedAlarm, 'pushButton', false);
            this.$set(this.selectedAlarm, 'acknowledge', false);
          }

          // Remove alarm circles for this alarm
          this.alarmCircles = this.alarmCircles.filter(circle => circle.imei_number !== alarm.imei_number);

          // Clear from active socket events
          this.activesocketEvents = localStorage.getItem('activesocketEvents') ? JSON.parse(localStorage.getItem('activesocketEvents')) : [];
          this.activesocketEvents = this.activesocketEvents.filter(event => event.imei_number !== alarm.imei_number);
          localStorage.setItem('activesocketEvents', JSON.stringify(this.activesocketEvents));

          this.stopAlarmSound();
          this.handleSuccess(this.$t('alarm_center_messages.alarm_resolved', { name: alarm.alarm_name }));

          // Refresh alarm list from backend
          this.getAlarmCenterGPSAlarms();
        }
      } catch (error) {
        this.$modal.hide("dialog");
        this.handleError(error);
      }
    },

    async copyLink(alarmImei) {
      try {
          let response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/gps/generateLink/${alarmImei}`);
        
          navigator.clipboard.writeText(response.data.link).then(() => {
            this.handleSuccess(this.$t('alarm_center_messages.link_copied'));
          })
          .catch(err => {
            this.handleError(this.$t('alarm_center_messages.copy_link_error'), err);
          });
        } catch (error) {
          this.handleError(error);
        }
    },

    generateBounds() {
      var vm = this;
      setTimeout(() => {
        vm.$refs.map.$mapPromise.then((map) => {
          const bounds = new vm.google.maps.LatLngBounds();
          for (let m of this.markers) {
            bounds.extend(m.position);
          }
          map.fitBounds(bounds);
        });
      }, 500);
    },

    checkMobile() {
      var vm = this;

      this.isMobile = window.innerWidth < 1024;

      if (this.isMobile) {
        vm.mobileMenuActive = false;
        vm.mapFullScreenControl = false;
      } else {
        this.mobileMenuActive = true;
        this.mapFullScreenControl = true;
      }
    },

    closeMenu() {
      this.mobileMenuActive = false;
    },

    generateMobileMenu() {
      //var vm = this;

      // let controlDiv = this.generateGoogleMapActionDiv();
      // this.mobileMenuDiv = controlDiv;

      // controlDiv.addEventListener("click", () => {
      //   vm.mobileMenuActive = true;
      // });

      // vm.$refs.map.$mapPromise.then((map) => {
      //   map.controls[vm.google.maps.ControlPosition.TOP_RIGHT].push(this.mobileMenuDiv);
      // });
    },
    handleSearch(query) {
      this.searchQuery = query.toLowerCase().trim();
    },

    toggleInput() {
      // Show or hide intput fields for test alarm.
      this.showTestInput = !this.showTestInput;
    },
    async activateTestAlarm () {

      /*
      Stores a timestamp in GPS colletion. Function for transfer alarm to test alarm column. 
      The alarm will stay in this column for 10 mins. If new alarm is received after the test,
      is considered as a new alarm and will show red.
      */
        let alarmIndex = this.gps_alarms.findIndex(alarm => {
          let phoneMatch = alarm.phone && alarm.phone.includes(this.inputPhone.trim());
          let passwordMatch = alarm.alarm_password && alarm.alarm_password.trim().toLowerCase() === this.inputPassword.trim().toLowerCase();
          return phoneMatch && passwordMatch;
        });

        if (alarmIndex !== -1) {
          try {
            // API call to store timestamp for the test alarm in GPS collection.
            const response = await this.axios.post(`${process.env.VUE_APP_SERVER_URL}/alarm/init-test/${this.gps_alarms[alarmIndex].imei_number}`);

            if (response.status === 200) {
              // Set testAlarmReceived to true, keep pushButton true for red state until user confirms test
              this.$set(this.gps_alarms[alarmIndex], 'initTestAlarm', true);
              this.$set(this.gps_alarms[alarmIndex], 'testConfirmed', false);
              this.$set(this.gps_alarms[alarmIndex], 'testAlarmReceived', true);
              // Do NOT clear pushButton here; let UI logic handle red state
              this.$set(this.gps_alarms[alarmIndex], 'acknowledge', false);

              this.inputPhone = '';
              this.inputPassword = '';

              this.handleSuccess(this.$t('alarm_center_messages.test_alarm_activated'));
              this.getAlarmCenterGPSAlarms(); //this.getActiveAlarm();
            } else {
              this.handleError(this.$t('alarm_center_messages.server_error', { status: response.status }));
            }

          } catch (error) {
            this.handleError(error);
          }
        } else {
          //console.error('Fel: IMEI eller lösenord matchar inte');
        }
    },
    prepareAlarmAudio() {
      if (!this.alarmAudio) {
        this.alarmAudio = new Audio(require('@/assets/audios/emergency-alarm.mp3'));
        this.alarmAudio.loop = true;
      }
      // Play then pause on first click to pass autoplay-policy
      this.alarmAudio.play()
        .then(() => {
          this.alarmAudio.pause();
          this.alarmAudio.currentTime = 0;
          this.alarmPrepared = true;
        })
        .catch(error => this.handleError(this.$t('alarm_center_messages.autoplay_prep_failed'), error));
    },
    playAlarmSound() {
      if (!this.alarmPrepared || !this.alarmAudio) {
        this.handleError(this.$t('alarm_center_messages.autoplay_prep_failed'));
        return;
      }

      try {
        setTimeout(() => {
          this.alarmAudio.play()
            .catch(error => this.handleError(this.$t('alarm_center_messages.sound_play_error'), error));
        }, 500);
      } catch (error) {
        this.handleError(this.$t('alarm_center_messages.sound_play_error'), error);
      }
    },

    stopAlarmSound() {
      if (this.alarmAudio) {
        this.alarmAudio.pause();
        this.alarmAudio.currentTime = 0; 
      }
    },
    toggleShowAlarm() {
      // To show or hide multiple markers on map.
      this.showAllAlarms = !this.showAllAlarms;
      if (this.showAllAlarms) {
        this.applyFullViewFilters();
      } else {
        this.applySingleAlarmFilters();
      }

    },
    
    toggleSound () {
      this.soundOn = !this.soundOn;
      if (!this.soundOn) {
        this.stopAlarmSound()
      }
    },
    formatEventType(type) {
      const mapping = {
        push_button: "Push Button",
        timer_alarm_on: "Timer Alarm On",
        timer_alarm_stop: "Timer Alarm Stop",
        man_down: "Man Down",
        mandown_alarm_on: "Man Down (On)",
        mandown_alarm_off: "Man Down (Off)"
      };
      return mapping[type] || type;
    },
    getEventType(imei) {
      // Do NOT modify state or access localStorage here!
      if (!imei || !this.activesocketEventsMap) return "";
      const eventType = this.activesocketEventsMap[imei];
      return eventType ? this.formatEventType(eventType) : "";
    },
    showTriggredAlarm(alarm, fromAPI = false) {
      this.addActiveAlarmImei(alarm.imei_number);

      // If alarm is in test mode, do not mark as pushButton
      if (alarm.initTestAlarm) {
        alarm.pushButton = false;
        // Do not touch highlight here
        return;
      }

      if (!alarm.testAlarmReceived && !fromAPI) {
        alarm.pushButton = true;
        alarm.lastPushButtonTimestamp = alarm.timestamp;
        // Do NOT set highlight here
      }

      let pushButtonTime = new Date(alarm.lastPushButtonTimestamp);
      let initTestTime = new Date(alarm.lastInitTestAlarmTimeStamp);
      if (
        (Date.now() - initTestTime) <= 10 * 60 * 1000 &&
        !alarm.testConfirmed &&
        initTestTime < pushButtonTime
      ) {
        alarm.testAlarmReceived = true;
        alarm.pushButton = false;
        // Do not touch highlight here
      }

      if (
        this.soundOn &&
        this.alarmPrepared &&
        !alarm.initTestAlarm &&
        !alarm.acknowledge
      ) {
        this.playAlarmSound();
      }
    },
    async getSecurPosVisit(phone) {
      try {
        if (phone) {
          let response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/securpos/getsecurpos/${phone}`);
          return response.data;
        }
        return { success: false, visit: null };
      } catch (error) {
        this.handleError(this.$t('alarm_center_messages.securpos_error'), error);
        return Promise.resolve({ success: false, visit: null });
      }
    },

    removeAlarmCircle(imei) {
      this.alarmCircles = this.alarmCircles.filter(circle => circle.imei_number !== imei);
    },
    addActiveAlarmImei(imei) {
      if (!this.activeAlarms.includes(imei)) {
        this.activeAlarms.push(imei);
      }
    },
    handleSocketEvent(data){

      let alarmCount = 0; 
      this.activesocketEvents = localStorage.getItem('activesocketEvents') ? JSON.parse(localStorage.getItem('activesocketEvents')) : [];

      for (let i = 0; i < this.gps_alarms.length; i++) {

        const alarm = this.gps_alarms[i];
        if (data.imei === alarm.imei_number) {

          // If alarm is in test mode, do not trigger pushButton logic
          if (alarm.initTestAlarm) {
            alarm.pushButton = false;
            break;
          }

          alarmCount++;
          this.showTriggredAlarm(alarm);
          // Prevent duplicate imei_number in activesocketEvents
          const idx = this.activesocketEvents.findIndex(
            e => e.imei_number === data.imei
          );

          if (idx !== -1) {
            // Update the existing entry
            this.activesocketEvents[idx].event_type = data.event_type;
          } else {
            // Add new entry
            this.activesocketEvents.push({
              imei_number: data.imei,
              event_type: data.event_type,
            });
          }
          break; // Exit loop early since match is found
        }
      }

      localStorage.setItem('activesocketEvents', JSON.stringify(this.activesocketEvents));
      
      setTimeout(()=>{
        if (alarmCount === 0) {
          this.stopAlarmSound();
        }
        this.resetSelectedList();
        this.getAlarmCenterGPSAlarms(); //this.getActiveAlarm();
      }, 500)
    },
    backToDashboard() {
      this.$router.push({ name: "alarm_central" });
    },
    initSocketClient() {
      var vm = this;
      this.socket = io(`${process.env.VUE_APP_ALARM_SERVER}/alarm_center_live`, {
        auth: {
          token: vm.$store.state.token
        },
      });

      this.socket.on("unauthorized", async (data) => {
        try {
          await this.refreshToken();
          this.socket.auth.token = this.token;
          if (this.token) {
            this.socket.connect();
            // retry last failed attempt
            if (data && data.length > 1) this.socket.emit(data[0], data[1]);
          }
        } catch (error) {
          this.handleError(error);
        }
      });

      this.socket.on("connect", () => {
          //console.log("connect:user= : ", this.user);
          if(this.user && this.user.role === 'admin') {
            setTimeout(() => {
              const roomName = `alarm_center_${this.alarm_center_id}`; // Or any unique room
              this.socket.emit("live_event_join_room", roomName);
              //console.log("live_event_join_room= : ", roomName);
            }, 1000);
            
          } 
      });

      this.socket.on("disconnect", (data) => {
        console.log("disconnect:", data);
      });

      this.socket.on("reset_connection_response", () => {
        setTimeout(() => {
          vm.checkConnection();
        }, 3000);
      });

      this.socket.on("live_event", (data) => {
        const allowedEvents = [
          "push_button",
          "timer_alarm",
          "timer_alarm_on",
          "timer_alarm_stop",
          "man_down",
          "mandown_alarm_on",
          "mandown_alarm_off"
        ];
        if (allowedEvents.includes(data.event_type)) {
          console.log("Live event :", data.event_type);
          this.handleSocketEvent(data);
        }
      });
    }
  },

  computed: {
    localVoiceStatus() {
      if (!this.selectedAlarm || !this.voiceCallAlarmId) return this.voiceCallStatus;
      return this.selectedAlarm.alarm_id === this.voiceCallAlarmId ? this.voiceCallStatus : 'idle';
    },
    formattedCallDuration() {
      const m = Math.floor(this.callDurationSeconds / 60).toString().padStart(2, '0');
      const s = (this.callDurationSeconds % 60).toString().padStart(2, '0');
      return `${m}:${s}`;
    },
    showOnlyEvent() {
      return this.alarm_center?.alarm_presentation_options === 'showOnlyEvent';
    },
    
    alarmPresentationText() {
      const option = this.alarm_center?.alarm_presentation_options;

      if (option === 'showOnlyEvent') {
        return this.$t('alarm_center_menu.show_only_active_event');
      } else if (option === 'showAll') {
        return this.$t('alarm_center_menu.show_all_alarms_mode');
      } else {
        return ''; // or fallback text  / Okänd visningsinställning
      }
    },
    gps_alarms_filtered() {
      let alarms = this.gps_alarms;

      // Step 2: Apply search filter
      alarms = alarms.filter(alarm => {
        const query = this.searchQuery?.toLowerCase();
        const matchesSearch = !query || (
          alarm.customer_name?.toLowerCase().includes(query) ||
          alarm.unit_name?.toLowerCase().includes(query) ||
          alarm.phone?.includes(this.searchQuery) ||
          alarm.imei_number?.includes(this.searchQuery) ||
          alarm.alarm_name?.toLowerCase().includes(query)
        );

        return (
          matchesSearch &&
          !alarm.acknowledge &&
          !alarm.testAlarmReceived &&
          !alarm.initTestAlarm
        );
      });

      // Step 3: Apply alarm presentation options
      const option = this.alarm_center?.alarm_presentation_options;
      if (option === 'showOnlyEvent') {
        alarms = alarms.filter(alarm => alarm.pushButton === true);
      }

      // Step 4: Corrected sort
      return alarms.sort((a, b) => {
        const pushCompare = Number(b.pushButton) - Number(a.pushButton);
        if (pushCompare !== 0) return pushCompare;
        return Number(b.highlight) - Number(a.highlight);
      });
    },
    provlarmList() {
      // Only alarms in test mode
      return this.gps_alarms.filter(a => a.initTestAlarm);
    },
    getAcknowledgedAlarms() {
      // Exclude alarms in test mode
      return this.gps_alarms.filter(
        alarm => alarm.acknowledge === true && !alarm.initTestAlarm
      );
    },
    google: gmapApi,
    filteredGPSAlarms() {
      // If no active search, return complete list of alarms with exceptions. 
      if (!this.searchQuery) {
        return (this.gps_alarms || []).filter((item) => !item.acknowledge && !item.initTestAlarm); // If no search query, return all items that is not acknowledged or in test state.
      } 
      // If active search - set input to lowercase and return match.
      return this.gps_alarms.filter((item) => {
        const searchQuery = this.searchQuery.toLowerCase();

        const matchesSearch =
            item.customer_name.toLowerCase().includes(searchQuery) ||
            item.unit_name.toLowerCase().includes(searchQuery) ||
            item.phone.includes(searchQuery) ||
            item.imei_number.includes(searchQuery) ||
            item.alarm_name.toLowerCase().includes(searchQuery);

        return matchesSearch && (!item.acknowledge && !item.initTestAlarm);
    });
    },
    filteredTestAlarms() {
      let filteredTestAlarms = this.gps_alarms.filter(alarm => alarm && alarm.initTestAlarm === true && !alarm.testConfirmed);
      return filteredTestAlarms;
    },
    hasValidInstructions() {
      const html = this.selectedAlarm.instructions || '';
      const textOnly = html.replace(/<[^>]*>/g, '').trim();
      return textOnly.length > 0;
    },
    activesocketEventsMap() {
      // Create a map for fast lookup without causing reactivity issues
      const map = {};
      (this.activesocketEvents || []).forEach(e => {
        map[e.imei_number] = e.event_type;
      });
      return map;
    },
  },

  created() {
    this.getAlarmCenterGPSAlarms();
    window.addEventListener("resize", this.checkMobile);
    setTimeout(() => {
      this.initSocketClient();
    }, 1000);
  },

  mounted() {
    this.refreshGPSData();
    this.generateMobileMenu();
    this.checkMobile();

    this.$nextTick(() => {
      if (this.$refs.map && this.$refs.map.$mapPromise) {
        this.$refs.map.$mapPromise.then((map) => {
          this.map = map;
        }).catch((err) => this.handleError("Error when loading Google Maps:", err));
      } 
    });
    window.addEventListener('click', () => {
      this.prepareAlarmAudio();
    }, { once: true });   

    const activesocketEvents = localStorage.getItem('activesocketEvents');
    if (activesocketEvents) {
      this.activesocketEvents = JSON.parse(activesocketEvents);
    } else {
      this.activesocketEvents = [];
      localStorage.setItem('activesocketEvents', "[]");
    }
  },

  beforeDestroy() {
    clearInterval(this.refreshIntervalRef);
    clearInterval(this.refreshActiveIntervalRef);
    window.removeEventListener("resize", this.checkMobile);
    this.stopAlarmSound();
    this.alarmAudio = null;
    this.alarmPrepared = false;
    // Clean up inline voice call if active
    if (this.jitsiApi) {
      this.jitsiApi.dispose();
      this.jitsiApi = null;
    }
    this.stopCallTimer();
  },
};
</script>
